import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AppService } from '../../../core/app.service';
import { BrandMarkComponent } from '../../widgets/brand-mark/brand-mark.component';
import { IconComponent } from '../../icon/icon.component';
import { RETURN_URL_KEY, clearMockAuthed, isMockAuthed } from '../../../core/auth/keycloak-auth.guard';
import { KeycloakPasswordAuthService } from '../../../core/auth/keycloak-password-auth.service';
import { RoleApiService } from '../../../data/identity/role-api.service';
import { PermissionsService } from '../../../core/permissions/permissions.service';
import { PERM, PermissionCode } from '../../../core/permissions/permission-codes';
import { PageTourService } from '../../../core/page-tour.service';
import { AgencyApiService } from '../../../data/agency/agency-api.service';

interface NavItem {
  id: string;
  /** ngx-translate key under {@code chrome.nav.*}. The template resolves it via the translate pipe. */
  labelKey: string;
  icon: string;
  disabled?: boolean;
  badge?: number;
  /** When set, the item is shown only if the caller holds this permission
   *  (platform admin always passes). Visibility is permission-based, not the
   *  coarse app.role() demo toggle. Untagged items are always shown. */
  perm?: PermissionCode;
  /** Shown if the caller holds ANY of these permissions — for surfaces reachable
   *  by more than one scope (e.g. Roles & Access: platform.roles.manage OR the
   *  agency-scoped agency.roles.manage). Platform admin still always passes. */
  anyPerm?: PermissionCode[];
  /** When true, the "platform admin bypasses every perm" shortcut is skipped
   *  for this item — the caller must actually hold {@link perm} / {@link anyPerm}.
   *  Used for the Issue Tracker: it's a Development-scope tool, not a Platform
   *  or Agency one, so it stays hidden even from platform admins unless the
   *  {@code issue.triage} perm is granted explicitly. */
  strictPerm?: boolean;
}
interface NavGroup {
  /** ngx-translate key under {@code chrome.navGroups.*}. Also used as the stable identifier
   *  for the collapsed-groups set — language switches don't reset open/closed state. */
  titleKey: string;
  items: NavItem[];
}

const NAV_GROUPS = (role: 'admin' | 'agent'): NavGroup[] => [
  { titleKey: 'chrome.navGroups.operations', items: [
    { id: 'dashboard', labelKey: 'chrome.nav.dashboard', icon: 'Dashboard' },
    { id: 'search', labelKey: 'chrome.nav.quickSearch', icon: 'Search' },
    { id: 'messages', labelKey: 'chrome.nav.messages', icon: 'ChatDots' },
    { id: 'tickets', labelKey: 'chrome.nav.tickets', icon: 'LifeBuoy' },
  ]},
  { titleKey: 'chrome.navGroups.insights', items: [
    { id: 'visualizer', labelKey: 'chrome.nav.visualizer', icon: 'Globe', perm: PERM.BOOKING_READ },
  ]},
  { titleKey: 'chrome.navGroups.inventory', items: [
    { id: 'flights-search', labelKey: 'chrome.nav.flights', icon: 'Plane', perm: PERM.OFFER_SEARCH },
    { id: 'trains-search', labelKey: 'chrome.nav.trains', icon: 'Train', perm: PERM.OFFER_SEARCH },
    { id: 'buses-search', labelKey: 'chrome.nav.buses', icon: 'Bus', perm: PERM.OFFER_SEARCH },
    { id: 'ground', labelKey: 'chrome.nav.ground', icon: 'Car', perm: PERM.OFFER_SEARCH },
    { id: 'hotels-search', labelKey: 'chrome.nav.hotels', icon: 'Hotel', anyPerm: [PERM.OFFER_SEARCH, PERM.BOOKING_READ] },
    { id: 'tours', labelKey: 'chrome.nav.tours', icon: 'Tour', anyPerm: [PERM.OFFER_SEARCH, PERM.BOOKING_READ] },
    { id: 'visa', labelKey: 'chrome.nav.visa', icon: 'Visa', perm: PERM.OFFER_SEARCH },
    { id: 'cip', labelKey: 'chrome.nav.cip', icon: 'Star', anyPerm: [PERM.OFFER_SEARCH, PERM.BOOKING_READ] },
    { id: 'sim', labelKey: 'chrome.nav.sim', icon: 'Sim', anyPerm: [PERM.OFFER_SEARCH, PERM.BOOKING_READ] },
    { id: 'insurance', labelKey: 'chrome.nav.insurance', icon: 'Shield', anyPerm: [PERM.OFFER_SEARCH, PERM.BOOKING_READ] },
  ]},
  { titleKey: 'chrome.navGroups.reservations', items: [
    { id: 'queue', labelKey: 'chrome.nav.pnrQueue', icon: 'Clock', badge: 6, perm: PERM.BOOKING_READ },
    { id: 'bookings', labelKey: 'chrome.nav.allBookings', icon: 'Inbox', perm: PERM.BOOKING_READ },
    { id: 'refunds', labelKey: 'chrome.nav.refunds', icon: 'Refund', badge: 5, perm: PERM.BOOKING_REFUND },
    { id: 'counter', labelKey: 'chrome.nav.counter', icon: 'Cash', perm: PERM.BOOKING_WRITE },
  ]},
  { titleKey: 'chrome.navGroups.customers', items: [
    // Customer directory is part of the booking workbench. Some legitimate
    // booking operators have booking.read without the newer customer.read
    // claim; keep the page reachable for either permission. The API still
    // enforces its own customer-service permissions when data is requested.
    { id: 'customers', labelKey: 'chrome.nav.customers', icon: 'Users',
      anyPerm: [PERM.CUSTOMER_READ, PERM.BOOKING_READ] },
    { id: 'customer-groups', labelKey: 'chrome.nav.customerGroups', icon: 'Users', perm: PERM.CUSTOMER_GROUP_READ },
    { id: 'loyalty', labelKey: 'chrome.nav.loyalty', icon: 'Star', perm: PERM.CUSTOMER_READ },
    // TEMP gate: mirror the `customers` item's audience so Discounts always shows
    // alongside the Customers directory, until identity seeds the dedicated
    // `customer.discount.read` perm — then restore perm: PERM.CUSTOMER_DISCOUNT_READ.
    { id: 'discounts', labelKey: 'chrome.nav.discounts', icon: 'Tag',
      anyPerm: [PERM.CUSTOMER_READ, PERM.BOOKING_READ] },
  ]},
  ...(role === 'admin' ? [{ titleKey: 'chrome.navGroups.supply', items: [
    // No dedicated supply.* permission exists yet — gate on platform.admin until
    // a supplier role is introduced (see REFACTOR-BACKLOG).
    { id: 'supply', labelKey: 'chrome.nav.supplyOverview', icon: 'Tag', perm: PERM.PLATFORM_ADMIN },
    { id: 'supply-charters', labelKey: 'chrome.nav.supplyCharterFlights', icon: 'Plane', perm: PERM.PLATFORM_ADMIN },
    { id: 'supply-trains', labelKey: 'chrome.nav.supplyCharterTrains', icon: 'Train', perm: PERM.PLATFORM_ADMIN },
    { id: 'supply-buses', labelKey: 'chrome.nav.supplyCharterBuses', icon: 'Bus', perm: PERM.PLATFORM_ADMIN },
    { id: 'supply-properties', labelKey: 'chrome.nav.supplyProperties', icon: 'Hotel', perm: PERM.PLATFORM_ADMIN },
    // Parked 2026-07-18 — "Room inventory & rate plans" (supply-rooms) is 100% mock and
    // duplicates Hotel Property → Rooms + Rates & Calendar. The Rate Plan Library concept
    // (scoped/global plans) has no backend model, and the Room Inventory portfolio-view has
    // no aggregation endpoint yet. Route + component kept intact so we can revive it once a
    // real multi-hotel roll-up ships. See CHANGELOG 2026-07-18.
    // { id: 'supply-rooms', labelKey: 'chrome.nav.supplyRooms', icon: 'Building', perm: PERM.PLATFORM_ADMIN },
    // Parked 2026-07-18 — the two "coming soon" SUPPLY entries below (Ancillaries + Allotment)
    // duplicate Hotel Property → Ancillary services and Hotel Property → Allotment & Holds. No
    // portfolio-wide backend + no dedicated screen; the sidebar chips were placeholder-only.
    // Kept commented so we can revive them once a real cross-hotel roll-up ships. See
    // CHANGELOG 2026-07-18.
    // { id: 'supply-ancillaries', labelKey: 'chrome.nav.supplyAncillaries', icon: 'Coffee', disabled: true, perm: PERM.PLATFORM_ADMIN },
    // { id: 'supply-allotment', labelKey: 'chrome.nav.supplyAllotment', icon: 'Clock', disabled: true, perm: PERM.PLATFORM_ADMIN },
  ]}] : []),
  // NETWORK — ONE permission-gated list (not split by admin/agent persona).
  // Every item is shown purely on the caller's permissions; the "Agencies" ↔
  // "Agency Profile" relabel for tenants is handled in groups(). Platform admin
  // bypasses the gates. (The old sub-agents item is dropped — the feature was
  // never wired; it was already commented out on the platform branch.)
  { titleKey: 'chrome.navGroups.network', items: [
    { id: 'agencies', labelKey: 'chrome.nav.agencies', icon: 'Building', perm: PERM.AGENCY_PROFILE_READ },
    { id: 'contract-templates', labelKey: 'chrome.nav.contractTemplates', icon: 'FileText', perm: PERM.AGENCY_CONTRACT_TEMPLATE_MANAGE },
    { id: 'companies', labelKey: 'chrome.nav.companies', icon: 'Building', perm: PERM.AGENCY_PROFILE_READ },
    { id: 'sale-channels', labelKey: 'chrome.nav.saleChannels', icon: 'Tag', perm: PERM.SALE_CHANNEL_READ },
    { id: 'signs', labelKey: 'chrome.nav.signs', icon: 'Tag', perm: PERM.SIGN_READ },
    { id: 'branches', labelKey: 'chrome.nav.branches', icon: 'Building', perm: PERM.AGENCY_PROFILE_READ },
    { id: 'markup', labelKey: 'chrome.nav.markupRules', icon: 'Tag', perm: PERM.AGENCY_MARKUP_EDIT },
    { id: 'agency-groups', labelKey: 'chrome.nav.agencyGroups', icon: 'Users', perm: PERM.AGENCY_GROUP_READ },
    // Channel-access / SIGN-access grant management consolidated into Partner
    // access (owner-offer model) — routes kept but removed from the sidebar.
    { id: 'partner-access', labelKey: 'chrome.nav.partnerAccess', icon: 'Shield', perm: PERM.AGENCY_PROFILE_READ },
  ]},
  { titleKey: 'chrome.navGroups.finance', items: [
    { id: 'wallet', labelKey: 'chrome.nav.walletLedger', icon: 'Wallet', perm: PERM.WALLET_READ },
    { id: 'invoices', labelKey: 'chrome.nav.invoices', icon: 'Receipt', perm: PERM.BILLING_INVOICE_READ },
    { id: 'reports', labelKey: 'chrome.nav.reports', icon: 'Chart', perm: PERM.ACCOUNTING_VOUCHER_READ },
    { id: 'voucher-review', labelKey: 'chrome.nav.voucherReview', icon: 'Check', perm: PERM.ACCOUNTING_VOUCHER_READ },
    { id: 'supplier-settlement', labelKey: 'chrome.nav.supplierSettlement', icon: 'Tag', perm: PERM.ACCOUNTING_VOUCHER_READ },
    { id: 'agency-ageing', labelKey: 'chrome.nav.agencyAgeing', icon: 'Clock', perm: PERM.BILLING_INVOICE_READ },
  ]},
  // ENTERTAINMENT — a light arcade to unwind between bookings. No perms; shown
  // to everyone. Number Match moved here from SYSTEM alongside more games. Sits
  // just above SYSTEM.
  { titleKey: 'chrome.navGroups.entertainment', items: [
    { id: 'village',      labelKey: 'chrome.nav.village',      icon: 'Map' },
    { id: 'wallboard',    labelKey: 'chrome.nav.wallboard',    icon: 'Board' },
    { id: 'number-match', labelKey: 'chrome.nav.numberMatch',  icon: 'Grid' },
    { id: 'memory-match', labelKey: 'chrome.nav.memoryMatch',  icon: 'Grid' },
    { id: 'minesweeper',  labelKey: 'chrome.nav.minesweeper',  icon: 'Bomb' },
    { id: 'chess',        labelKey: 'chrome.nav.chess',        icon: 'Chess' },
    { id: 'game-2048',    labelKey: 'chrome.nav.game2048',     icon: 'Grid2048' },
    { id: 'sudoku',       labelKey: 'chrome.nav.sudoku',       icon: 'Sudoku' },
    { id: 'connect-four', labelKey: 'chrome.nav.connectFour',  icon: 'Grid7' },
    { id: 'quiz',         labelKey: 'chrome.nav.quiz',         icon: 'Quiz' },
  ]},
  // SYSTEM — ONE permission-gated list. Roles & Access + Users are reachable by
  // BOTH platform staff (platform.* perms) and an Agency Admin managing their own
  // agency (agency.roles.manage / agency.users.manage); the rbac screen is already
  // scope-aware and auto-scopes an agency admin to their agency.
  { titleKey: 'chrome.navGroups.system', items: [
    { id: 'reference-data', labelKey: 'chrome.nav.referenceData', icon: 'Tag', perm: PERM.CONTENT_REFERENCE_READ },
    { id: 'data-simulator', labelKey: 'chrome.nav.dataSimulator', icon: 'Spark', perm: PERM.PLATFORM_ADMIN },
    { id: 'rbac', labelKey: 'chrome.nav.rbac', icon: 'Shield', anyPerm: [PERM.PLATFORM_ROLES_MANAGE, PERM.AGENCY_ROLES_MANAGE] },
    { id: 'users', labelKey: 'chrome.nav.users', icon: 'Users', anyPerm: [PERM.PLATFORM_USERS_MANAGE, PERM.AGENCY_USERS_MANAGE] },
    // Platform billing-plan ADMIN — gate on write, not read: agency users hold
    // billing.plan.read for their own invoices (FINANCE group) but must not see
    // the platform plan-management screen.
    { id: 'billing', labelKey: 'chrome.nav.billing', icon: 'Cash', perm: PERM.BILLING_PLAN_WRITE },
    // Internal issue tracker — Development scope only. {@code strictPerm}
    // makes the sidebar skip the "platform admin sees all" shortcut, so PA
    // won't see this row unless {@code issue.triage} is granted explicitly.
    { id: 'issues', labelKey: 'chrome.nav.issueTracker', icon: 'Bug', perm: PERM.ISSUE_TRIAGE, strictPerm: true },
    // "My Reported Issues" — every signed-in user's own inbox. No perm gate;
    // Platform + Agency + dev all see it, matching the "anyone can report" flow.
    { id: 'my-issues', labelKey: 'chrome.nav.myReportedIssues', icon: 'Flag' },
    { id: 'settings', labelKey: 'chrome.nav.settings', icon: 'Settings', perm: PERM.PLATFORM_SETTINGS_MANAGE },
  ]},
];

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [BrandMarkComponent, IconComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  app = inject(AppService);
  router = inject(Router);
  private readonly oidc = inject(OidcSecurityService);
  private readonly passwordAuth = inject(KeycloakPasswordAuthService);
  private readonly roleApi = inject(RoleApiService);
  private readonly perms = inject(PermissionsService);
  private readonly agencyApi = inject(AgencyApiService);
  readonly pageTour = inject(PageTourService);

  /**
   * Product access is commercial agency configuration, not an RBAC permission.
   * Keep it separate from PermissionsService: an agency user may have the
   * generic offer.search permission while still being disabled for a product.
   * Undefined means the agency record is still loading (or could not be read),
   * so a transient request failure never locks a user out of the whole menu.
   */
  private readonly agencyProducts = signal<Set<string> | undefined>(undefined);
  private agencyProductsKey = '';

  /**
   * True for callers who browse the whole agency directory (platform admin +
   * cross-tenant onboarding / KYC staff). Agency-scope users are everyone
   * else — for them the "Agencies" nav entry is really their own agency, so
   * we relabel it "Agency Profile". Permission-based, mirroring the backend
   * {@code TenantScope.hasUnrestrictedAgencyRead} and the agency-network
   * screen's own guard.
   */
  // Platform staff (can read agencies, own none) see the directory → "Agencies";
  // agency-tenant users (own their agency) see their own → "Agency Profile".
  // Scope-based so it covers every platform role (Finance, KYC, Onboarding,
  // Contracts, …) without an allow-list.
  private readonly canBrowseDirectory = computed<boolean>(() =>
    this.perms.isPlatformAdmin()
    || (this.perms.has(PERM.AGENCY_PROFILE_READ) && this.perms.agencyIds().size === 0));

  /**
   * Which nav branch to render — derived from PERMISSIONS, not the `app.role()`
   * demo toggle. Platform staff (no agency ownership) get the platform-ops nav
   * ('admin'): Supply, the full Network directory, and System. Agency-tenant
   * users get the agency self-service nav ('agent'): My Agency + My Sub-Agencies.
   * Per-item `perm` gating still applies on top, so each user only sees what
   * they hold. Same discriminator as {@link canBrowseDirectory}.
   */
  private readonly navPersona = computed<'admin' | 'agent'>(() =>
    this.canBrowseDirectory() ? 'admin' : 'agent');

  /**
   * The signed-in user's real assigned platform-role label (e.g. "Onboarding
   * Officer"), fetched from {@code /platform-users/me}. The JWT only carries
   * the coarse Keycloak realm role ("AGENCY_ADMIN"), which would otherwise
   * mislabel every non-admin officer as "Agency Admin". Null until resolved
   * (or if the caller has no platform-role grant) — displayRole falls back to
   * the realm-role heuristic then.
   */
  private readonly myRoleLabel = signal<string | null>(null);
  private meFetchedFor: string | null = null;

  /** True when every agency this caller belongs to is a STANDALONE-accounting
   *  tenant — i.e. no OTA modules were licensed at onboarding. Drives the
   *  sidebar allowlist below. Platform admins are never treated as
   *  standalone (they cross agencies). undefined while the agency records
   *  are still loading — matches the {@code agencyProducts} initial state
   *  so the sidebar doesn't flash the OTA groups before the answer arrives. */
  private readonly isStandaloneTenant = signal<boolean | undefined>(undefined);

  constructor() {
    effect(() => {
      const ids = [...this.perms.agencyIds()].sort();
      const key = ids.join(',');
      if (this.perms.isPlatformAdmin() || key === this.agencyProductsKey) return;
      this.agencyProductsKey = key;
      this.agencyProducts.set(undefined);
      this.isStandaloneTenant.set(undefined);
      if (!ids.length) {
        this.agencyProducts.set(new Set());
        this.isStandaloneTenant.set(false);
        return;
      }
      forkJoin(ids.map((id) => this.agencyApi.get(id))).subscribe({
        next: (agencies) => {
          const access = new Set<string>();
          for (const agency of agencies) {
            for (const product of agency.productAccess ?? []) access.add(this.normaliseProduct(product));
          }
          this.agencyProducts.set(access);
          // STANDALONE detection: every agency the caller belongs to must
          // have accountingMode === 'STANDALONE'. Any single OTA agency in
          // the set means we can't treat the whole session as standalone
          // (a multi-agency operator still needs the OTA groups for the
          // OTA one). Falls back to inspecting productAccess for pre-
          // accountingMode rows.
          const standalone = agencies.every((a) => {
            const mode = (a as { accountingMode?: string }).accountingMode;
            if (mode) return mode === 'STANDALONE';
            const products = a.productAccess ?? [];
            return products.length > 0
                && products.every((p) => this.normaliseProduct(p) === 'accounting');
          });
          this.isStandaloneTenant.set(standalone);
        },
        error: () => {
          this.agencyProducts.set(undefined);
          this.isStandaloneTenant.set(undefined);
        },
      });
    }, { allowSignalWrites: true });

    // Fetch the caller's own staff record once a real (ROPC) session exists.
    effect(() => {
      const sub = this.passwordAuth.profile()?.sub;
      if (!sub || this.meFetchedFor === sub) return;
      this.meFetchedFor = sub;
      this.roleApi.me().subscribe({
        next: (u) => {
          const a = u.assignments?.[0];
          if (!a) return;
          this.myRoleLabel.set(a.scopeAgencyName ? `${a.roleName} · ${a.scopeAgencyName}` : a.roleName);
        },
        // No platform-role grant (e.g. pure agency user) or endpoint miss →
        // keep the realm-role fallback; not fatal to the chrome.
        error: () => {},
      });
    });
  }

  private normaliseProduct(product: string): string {
    const value = String(product ?? '').trim().toLowerCase();
    return ({
      flight: 'flights', flights: 'flights',
      train: 'trains', trains: 'trains',
      bus: 'buses', buses: 'buses',
      hotel: 'hotels', hotels: 'hotels',
      tour: 'tours', tours: 'tours', tour_package: 'tours', tour_packages: 'tours',
      'tour-package': 'tours', 'tour-packages': 'tours',
      visa: 'visa', cip: 'cip', cip_service: 'cip', cip_services: 'cip',
      'cip-service': 'cip', 'cip-services': 'cip',
      transfer: 'ground', ground: 'ground',
      sim: 'sim', travel_sim: 'sim', sim_card: 'sim', esim: 'sim',
      'travel-sim': 'sim',
      insurance: 'insurance', travel_insurance: 'insurance',
      'travel-insurance': 'insurance',
    } as Record<string, string>)[value] ?? value;
  }

  private productEnabled(itemId: string): boolean {
    if (!['flights-search', 'trains-search', 'buses-search', 'ground', 'hotels-search',
      'tours', 'visa', 'cip', 'sim', 'insurance'].includes(itemId)) return true;
    const products = this.agencyProducts();
    const productId = itemId.replace('-search', '');
    return products === undefined || products.has(productId);
  }

  /** Items a STANDALONE-accounting tenant is allowed to see in the sidebar.
   *  Everything OTA-shaped (reservations, customers, supply, sale-channels,
   *  signs, agency-groups, markup, entertainment, insights) is hidden —
   *  the tenant doesn't sell travel, so those workflows have no reason to
   *  render. Finance items stay because they ARE accounting; own-agency
   *  profile stays so the operator can still edit their commercial /
   *  contract data from the b2b console. */
  private static readonly STANDALONE_ALLOWED_ITEMS = new Set<string>([
    'dashboard',
    'messages', 'tickets',
    'agencies', 'contract-templates',
    'wallet', 'invoices', 'reports', 'voucher-review',
    'supplier-settlement', 'agency-ageing',
  ]);

  private allowedForStandalone(itemId: string): boolean {
    // Fetch still in flight → be permissive, avoid the OTA groups flashing
    // then disappearing. The productEnabled gate below still hides inventory
    // items during the load window (agencyProducts is undefined then too).
    const standalone = this.isStandaloneTenant();
    if (standalone !== true) return true;
    return SidebarComponent.STANDALONE_ALLOWED_ITEMS.has(itemId);
  }

  groups = computed(() => {
    const raw = NAV_GROUPS(this.navPersona());
    const canBrowse = this.canBrowseDirectory();
    const isAdmin = this.perms.isPlatformAdmin();
    return raw
      .map((g) => ({
        ...g,
        items: g.items
          // Permission-gate items that declare a `perm`/`anyPerm` (platform admin
          // bypasses, unless the item is marked {@code strictPerm} — used for
          // dev-only tools like Issue Tracker that must stay hidden from PA too).
          // Untagged items are baseline utilities shown to everyone.
          .filter((it) => {
            // Product access is enforced only for agency-scope users. Platform
            // staff can browse every inventory product regardless of tenant
            // commercial configuration.
            if (this.navPersona() === 'agent' && !this.productEnabled(it.id)) return false;
            // STANDALONE-accounting tenants only see the allowlisted items —
            // everything OTA-shaped is hidden even when the caller has the
            // permission for it (they're onboarded as accounting-only, so
            // reservations / customers / supply etc. aren't part of the deal).
            if (this.navPersona() === 'agent' && !this.allowedForStandalone(it.id)) return false;
            if (!it.perm && !it.anyPerm) return true;
            if (isAdmin && !it.strictPerm) return true;
            if (it.perm && this.perms.has(it.perm)) return true;
            return !!it.anyPerm?.some((p) => this.perms.has(p));
          })
          // Agency-scope users see their own agency, so relabel "Agencies".
          .map((it) => (it.id === 'agencies' && !canBrowse)
            ? { ...it, labelKey: 'chrome.nav.agencyProfile' } : it),
      }))
      .filter((g) => g.items.length > 0);
  });

  /**
   * Real signed-in user rendered in the footer. Prefers the decoded JWT
   * profile from the ROPC session; when the user hasn't logged in through
   * Keycloak (dev-mock guard bypass, OIDC-code-flow path we haven't wired
   * yet) we fall back to the historical placeholder strings so the chrome
   * still renders sensibly.
   */
  readonly displayName = computed(() => {
    const p = this.passwordAuth.profile();
    if (p) return p.name || p.preferredUsername || p.email || 'Signed in';
    return this.app.role() === 'admin' ? 'Ava Doyle' : 'Ahmed Hassan';
  });

  readonly displayRole = computed(() => {
    // Prefer the real assigned platform role resolved from identity; the JWT
    // realm role below is only a coarse fallback (mislabels officers as admin).
    const real = this.myRoleLabel();
    if (real) return real;
    const p = this.passwordAuth.profile();
    if (p) {
      // Prioritise the recognisable app-level roles when Keycloak has
      // stamped them; fall back to email so the footer still identifies
      // the user in tenants that haven't finished role assignment yet.
      const roles = p.realmRoles;
      // Keycloak realm roles are UPPERCASE snake case (PLATFORM_ADMIN),
      // not the lowercase we render in the UI. Match on both so this
      // works against any KC realm variant.
      const has = (name: string) => roles.includes(name) || roles.includes(name.toLowerCase())
        || roles.includes(name.replace('_', '-').toLowerCase());
      if (has('PLATFORM_ADMIN') || has('ADMIN')) return 'Platform Admin';
      if (has('AGENCY_ADMIN')) return 'Agency Admin';
      if (has('SUBAGENT_MANAGER')) return 'Sub-Agent Manager';
      if (has('SUBAGENT')) return 'Sub-Agent';
      if (has('AGENT')) return 'Agent';
      return p.email || 'Signed in user';
    }
    return this.app.role() === 'admin' ? 'Platform Admin' : 'Falcon Travel · Gold';
  });

  readonly displayInitials = computed(() => {
    const p = this.passwordAuth.profile();
    if (p) return p.initials;
    return this.app.role() === 'admin' ? 'AD' : 'AH';
  });

  /** Collapsed groups are tracked by titleKey — a stable identifier that survives
   *  language switches (so toggling EN → FA doesn't expand groups the user closed).
   *  Initial state: every group starts collapsed except the first (Operations),
   *  so the nav opens compact and the user expands the section they need. */
  readonly collapsedGroups = signal<Set<string>>(
    // Seed from the superset ('admin') so every possible group starts collapsed
    // regardless of which persona the caller resolves to.
    new Set(NAV_GROUPS('admin').slice(1).map((g) => g.titleKey)),
  );

  isCollapsed(titleKey: string): boolean {
    return this.collapsedGroups().has(titleKey);
  }

  toggleGroup(titleKey: string): void {
    this.collapsedGroups.update((s) => {
      const next = new Set(s);
      if (next.has(titleKey)) next.delete(titleKey); else next.add(titleKey);
      return next;
    });
  }

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private route = computed(() => {
    const u = this.currentUrl();
    return (u || '/').replace(/^\//, '').split('?')[0] || 'dashboard';
  });

  isActive(id: string): boolean {
    const r = this.route();
    if (r === id) return true;
    if (id === 'flights-search' && r.startsWith('flights')) return true;
    if (id === 'trains-search' && r.startsWith('trains')) return true;
    if (id === 'buses-search' && r.startsWith('buses')) return true;
    if (id === 'cip' && r.startsWith('cip')) return true;
    if (id === 'hotels-search' && r.startsWith('hotels')) return true;
    if (id === 'supply' && r === 'supply') return true;
    if (id === 'supply-properties' && r.startsWith('supply-propert')) return true;
    // 'agencies' owns the whole agency-management surface (network directory,
    // onboarding wizard, KYC queue, profile). 'agents' stays exact-match so
    // it isn't swallowed by the agency-* prefix.
    if (id === 'agencies' && (r === 'agencies' || r.startsWith('agency-'))) return true;
    return false;
  }

  navigate(id: string) {
    this.app.navigate(id);
  }

  /**
   * End the Keycloak session and clear local auth state.
   *
   * Flow: oidc.logoff() POSTs to Keycloak's end-session endpoint, which kills
   * the Keycloak SSO cookie + tokens server-side, then redirects the browser
   * to {@code postLogoutRedirectUri} (configured in keycloak-auth.config.ts as
   * the app root). Root redirects to /dashboard, which is protected — the
   * guard sees no auth and bounces to Keycloak's login. Net effect: user
   * clicks Logout → ends up on the Keycloak login screen, ready to sign in
   * as a different user.
   */
  logout(): void {
    try {
      sessionStorage.removeItem(RETURN_URL_KEY);
    } catch {
      /* private mode — nothing to clean */
    }
    const mock = isMockAuthed();
    const wasPassword = this.passwordAuth.isAuthenticated();
    clearMockAuthed();
    this.passwordAuth.logout();
    if (mock || wasPassword) {
      // Neither the mock login nor the direct-password (ROPC) login ever
      // registered a session on Keycloak's side, so `oidc.logoff()` would
      // just bounce the user to Keycloak with no session to end. Land
      // them on the sign-in page directly.
      this.router.navigateByUrl('/signin');
      return;
    }
    this.oidc.logoff().subscribe();
  }
}
